import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaSearch, FaFilter, FaTimes, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useUser } from "../context/UserContext";
import { useLanguage } from "../context/LanguageContext";
import toast, { Toaster } from 'react-hot-toast';
import { fetchAllCourses } from "../services/courseService";
import CourseCard from "../components/CourseCard";

const Courses = () => {
  const { language } = useLanguage();
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    faculty: "",
    level: "",
    category: ""
  });
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const { user } = useUser();

  // محتوى متعدد اللغات
  const content = {
    en: {
      title: "Available Courses",
      searchPlaceholder: "Search courses...",
      noResults: "No courses match your search.",
      loading: "Loading...",
      error: "Error loading courses. Please try again.",
      showFilters: "Filters",
      hideFilters: "Hide Filters",
      searchButton: "Search",
      clearSearch: "Clear",
      filters: {
        faculty: "Faculty",
        level: "Level",
        category: "Category",
        all: "All",
        engineering: "Engineering",
        science: "Science",
        design: "Design",
        beginner: "Beginner",
        intermediate: "Intermediate",
        advanced: "Advanced"
      },
      sortOptions: {
        newest: "Newest",
        highestRated: "Highest Rated",
        mostPopular: "Most Popular"
      }
    },
    ar: {
      title: "الدورات المتاحة",
      searchPlaceholder: "ابحث في الدورات...",
      noResults: "لا توجد مواد مطابقة للبحث.",
      loading: "جاري التحميل...",
      error: "حدث خطأ أثناء جلب الدورات. يرجى المحاولة مرة أخرى.",
      showFilters: "الفلاتر",
      hideFilters: "إخفاء الفلاتر",
      searchButton: "بحث",
      clearSearch: "مسح",
      filters: {
        faculty: "الكلية",
        level: "المستوى",
        category: "التصنيف",
        all: "الكل",
        engineering: "هندسة",
        science: "علوم",
        design: "تصميم",
        beginner: "مبتدئ",
        intermediate: "متوسط",
        advanced: "متقدم"
      },
      sortOptions: {
        newest: "الأحدث",
        highestRated: "الأعلى تقييماً",
        mostPopular: "الأكثر شيوعاً"
      }
    }
  };

  const t = content[language];

  const fetchCourses = async () => {
    try {
      const coursesList = await fetchAllCourses();
      setCourses(coursesList);
    } catch (error) {
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, [language]);

  const filteredCourses = courses.filter((course) => {
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         course.instructor.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFaculty = filters.faculty ? course.faculty === filters.faculty : true;
    const matchesLevel = filters.level ? course.level === filters.level : true;
    const matchesCategory = filters.category ? course.category === filters.category : true;
    
    return matchesSearch && matchesFaculty && matchesLevel && matchesCategory;
  });

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      faculty: "",
      level: "",
      category: ""
    });
    setSearchQuery("");
    setShowMobileSearch(false);
  };

  const toggleMobileSearch = () => {
    setShowMobileSearch(!showMobileSearch);
    if (showMobileSearch) {
      setSearchQuery("");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-xl font-semibold text-gray-700">{t.loading}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Mobile Optimized */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 truncate">
              {t.title}
            </h1>
            
            {/* Mobile Search Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={toggleMobileSearch}
                className="md:hidden p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                aria-label="Toggle search"
              >
                {showMobileSearch ? <FaTimes /> : <FaSearch />}
              </button>
              
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm"
              >
                <FaFilter className="text-xs" />
                <span className="hidden sm:inline">{showFilters ? t.hideFilters : t.showFilters}</span>
                {showFilters ? <FaChevronUp className="text-xs" /> : <FaChevronDown className="text-xs" />}
              </button>
            </div>
          </div>

          {/* Mobile Search Bar */}
          {showMobileSearch && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-4 md:hidden"
            >
              <div className="relative">
                <input
                  type="text"
                  placeholder={t.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full p-3 pl-10 pr-20 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-base"
                  autoFocus
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded text-gray-600"
                  >
                    {t.clearSearch}
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Desktop Search and Filters */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="hidden md:block bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex gap-4">
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder={t.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-3 pl-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Filters Panel - Mobile Optimized */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-lg shadow-sm p-4 mb-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.filters.faculty}
                </label>
                <select
                  value={filters.faculty}
                  onChange={(e) => handleFilterChange('faculty', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-base appearance-none bg-white"
                >
                  <option value="">{t.filters.all}</option>
                  <option value="engineering">{t.filters.engineering}</option>
                  <option value="science">{t.filters.science}</option>
                  <option value="design">{t.filters.design}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.filters.level}
                </label>
                <select
                  value={filters.level}
                  onChange={(e) => handleFilterChange('level', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-base appearance-none bg-white"
                >
                  <option value="">{t.filters.all}</option>
                  <option value="beginner">{t.filters.beginner}</option>
                  <option value="intermediate">{t.filters.intermediate}</option>
                  <option value="advanced">{t.filters.advanced}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.filters.category}
                </label>
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-base appearance-none bg-white"
                >
                  <option value="">{t.filters.all}</option>
                  <option value="programming">
                    {language === 'ar' ? 'برمجة' : 'Programming'}
                  </option>
                  <option value="mathematics">
                    {language === 'ar' ? 'رياضيات' : 'Mathematics'}
                  </option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={resetFilters}
                  className="w-full py-3 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors font-medium text-base"
                >
                  {language === 'ar' ? "مسح الفلاتر" : "Reset Filters"}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Results Count */}
      <div className="max-w-7xl mx-auto px-4 mb-4">
        <div className="text-sm text-gray-600">
          {language === 'ar' 
            ? `عدد النتائج: ${filteredCourses.length}`
            : `${filteredCourses.length} courses found`
          }
        </div>
      </div>

      {/* Courses Grid - Mobile Optimized */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        {filteredCourses.length > 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6"
          >
            {filteredCourses.map((course, index) => (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="transform hover:scale-[1.02] transition-transform duration-200"
              >
                <CourseCard 
                  course={course} 
                  isLoggedIn={!!user}
                  language={language}
                />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center py-12"
          >
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <FaSearch className="text-2xl text-gray-400" />
              </div>
              <div className="text-gray-500 text-lg mb-2">{t.noResults}</div>
              <div className="text-sm text-gray-400 max-w-md mx-auto">
                {language === 'ar' 
                  ? 'جرب تغيير كلمات البحث أو الفلاتر'
                  : 'Try adjusting your search terms or filters'
                }
              </div>
            </div>
            <div className="space-y-3 sm:space-y-0 sm:space-x-3 sm:flex sm:justify-center">
              <button
                onClick={resetFilters}
                className="w-full sm:w-auto px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                {language === 'ar' ? "عرض جميع الدورات" : "Show All Courses"}
              </button>
              {(searchQuery || filters.faculty || filters.level || filters.category) && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setFilters({ faculty: "", level: "", category: "" });
                  }}
                  className="w-full sm:w-auto px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  {language === 'ar' ? "مسح البحث" : "Clear Search"}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {/* Toast Container */}
      <Toaster 
        position="bottom-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
    </div>
  );
};

export default Courses;